import axios from 'axios'
import {AdminAlert} from '../AdminAlert/AdminAlert'
import React, {useEffect, useState} from 'react'
import {PasswordSecurityFields, validatePasswordFields, passwordPayload} from '../ChangePasswordForm/ChangePasswordForm'

const fiscalRegimeNames = {
  605: 'Sueldos y Salarios e Ingresos Asimilados a Salarios.',
  606: 'Arrendamiento.',
  607: 'Régimen de Enajenación o Adquisición de Bienes.',
  608: 'Demás ingresos.',
  611: 'Ingresos por Dividendos (socios y accionistas).',
  612: 'Personas Físicas con Actividades Empresariales y Profesionales.',
  625: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas.',
  626: 'Régimen Simplificado de Confianza (RESICO).',
}

const generalFields = [
  ['Régimen fiscal', 'regimenFiscal'], ['Nombre comercial', 'nombreComercial'],
  ['Razón social', 'razonSocial'], ['RFC', 'rfc'], ['Nombre de contacto', 'nombreContacto'],
  ['Teléfono', 'telefono'], ['Celular', 'celular'], ['Correo electrónico', 'email'],
  ['CURP', 'curp'], ['Calle', 'calle'], ['No. exterior', 'exterior'],
  ['No. interior', 'interior'], ['Colonia', 'colonia'], ['Código postal', 'cp'],
  ['Delegación/municipio', 'delegacion'], ['Estado', 'estado'], ['País', 'pais'],
]

const documentFields = [
  ['Identificación oficial frente', 'identificacionFrente'],
  ['Identificación oficial vuelta', 'identificacionVuelta'],
  ['Comprobante de domicilio', 'comprobanteDomicilio'],
  ['Constancia de situación fiscal', 'constanciaSituacionFiscal'],
  ['Fotografía del inmueble 1', 'fotografiaInmueble1'],
  ['Fotografía del inmueble 2', 'fotografiaInmueble2'],
  ['Fotografía del inmueble 3', 'fotografiaInmueble3'],
  ['RFC (documento histórico)', 'rfcFile'],
]

const requiredDocumentFields = new Set([
  'identificacionFrente',
  'identificacionVuelta',
  'comprobanteDomicilio',
  'constanciaSituacionFiscal',
  'fotografiaInmueble1',
])

function ModalDistribuidor({usuario, vendedores, listasPrecios, onClose, onUpdated}) {
  const distributor = usuario && usuario[0]
  const API_URL = process.env.REACT_APP_API_URL
  const [form, setForm] = useState({})
  const [manualActivation, setManualActivation] = useState(false)
  const [activationToast, setActivationToast] = useState(null)
  const [saving, setSaving] = useState(false)
  const [activating, setActivating] = useState(false)
  const [message, setMessage] = useState(null)
  const [passwords, setPasswords] = useState({password: '', confirmPassword: ''})

  useEffect(() => {
    if (!distributor) return
    setManualActivation(false)
    setForm({
      estatus: ['Por Activar', 'Activo', 'Inactivo'].includes(distributor.estatus) ? distributor.estatus : 'Por Activar',
      idVendedor: distributor.idVendedor || '',
      idListaPrecios: distributor.idListaPrecios || '',
      diasCredito: distributor.diasCredito ?? 0,
      cascadaDescuentos: String(distributor.cascadaDescuentos ?? ''),
      condicionado1: distributor.condicionado1 ?? 0,
      condicion1: distributor.condicion1 || '',
      condicionado2: distributor.condicionado2 ?? 0,
      condicion2: distributor.condicion2 || '',
      descuentoTotal: distributor.descuentoTotal ?? 0,
      mostrarDescuentoTotal: Boolean(distributor.mostrarDescuentoTotal),
    })
    setMessage(null)
    setPasswords({password: '', confirmPassword: ''})
  }, [distributor])

  if (!distributor) return null

  const updateField = (field, value) => setForm((current) => ({...current, [field]: value}))
  const generalFieldValue = (field) => field === 'regimenFiscal'
    ? fiscalRegimeNames[distributor[field]] || distributor[field] || ''
    : distributor[field] || ''
  const nonNegativeFields = ['diasCredito', 'condicionado1', 'condicionado2', 'descuentoTotal']
  const activationMessage = distributor.estatus === 'Por Activar'
    ? 'El distribuidor aún debe crear su contraseña para completar la activación de su cuenta.'
    : distributor.estatus === 'Inactivo'
      ? 'La cuenta del distribuidor está inactiva. Si deseas habilitar nuevamente su acceso, cambia el estatus a Activo.'
      : 'El distribuidor ya completó el proceso de activación de su cuenta. Si deseas desactivarla, cambia el estatus a Inactivo.'

  const validate = () => {
    if (!form.idVendedor || !form.idListaPrecios) return 'Selecciona un vendedor y una lista de precios.'
    if (!['Por Activar', 'Activo', 'Inactivo'].includes(form.estatus)) return 'Selecciona un estatus válido.'
    if (nonNegativeFields.some((field) => form[field] === '' || Number(form[field]) < 0 || Number.isNaN(Number(form[field])))) {
      return 'Los días, importes y porcentajes deben ser números mayores o iguales a cero.'
    }
    if (form.cascadaDescuentos.length > 255) return 'La cascada de descuentos no puede exceder 255 caracteres.'
    if (form.condicion1.length > 255 || form.condicion2.length > 255) return 'Las condiciones no pueden exceder 255 caracteres.'
    return validatePasswordFields(passwords.password, passwords.confirmPassword, false)
  }

  const saveCommercialDetails = async () => {
    setMessage(null)
    setActivationToast(null)
    const validationError = validate()
    if (validationError) {
      setActivationToast({type: 'danger', text: validationError})
      return
    }
    setSaving(true)
    setMessage(null)
    try {
      const data = new FormData()
      data.append('idUsuario', distributor.id)
      Object.entries(form).forEach(([key, value]) => data.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : value))
      Object.entries(passwordPayload(passwords.password, passwords.confirmPassword)).forEach(([key, value]) => data.append(key, value))
      await axios.post(`${API_URL}/actualizar-detalles-comerciales`, data)
      setPasswords({password: '', confirmPassword: ''})
      await onUpdated(distributor.id)
      setActivationToast({type: 'success', text: 'Detalles comerciales guardados correctamente.'})
    } catch (error) {
      setActivationToast({type: 'danger', text: error.response?.data?.error || 'No fue posible guardar los detalles comerciales.'})
    } finally {
      setSaving(false)
    }
  }

  const activateAccount = async () => {
    if (!form.idVendedor) {
      setMessage({type: 'danger', text: 'Selecciona y guarda un vendedor antes de habilitar la cuenta.'})
      return
    }
    setActivating(true)
    setMessage(null)
    try {
      const data = new FormData()
      data.append('idUsuario', distributor.id)
      data.append('idVendedor', form.idVendedor)
      await axios.post(`${API_URL}/autorizar-distribuidor`, data)
      await onUpdated(distributor.id)
      setMessage({type: 'success', text: 'Cuenta enviada a activación correctamente.'})
    } catch (error) {
      setMessage({type: 'danger', text: error.response?.data?.message || error.response?.data?.error || 'No fue posible habilitar la cuenta.'})
    } finally {
      setActivating(false)
    }
  }

  const activateManually = async () => {
    const validationError = validatePasswordFields(passwords.password, passwords.confirmPassword)
    if (validationError) {
      setMessage({type: 'danger', text: validationError})
      return
    }
    setActivating(true)
    setMessage(null)
    try {
      await axios.put(`${API_URL}/usuarios-password-reset/${distributor.id}`, {
        newPassword: passwords.password,
        confirmPassword: passwords.confirmPassword,
        activateManually: true,
      })
      setPasswords({password: '', confirmPassword: ''})
      await onUpdated(distributor.id)
      setManualActivation(false)
      setActivationToast({type: 'success', text: 'Distribuidor activado correctamente.'})
    } catch (error) {
      setMessage({type: 'danger', text: error.response?.data?.message || 'No fue posible activar al distribuidor.'})
    } finally {
      setActivating(false)
    }
  }

  return (
    <div className='modal-distribuidor'>
      <AdminAlert message={activationToast} onClose={setActivationToast} />
      <section className='distributor-section'>
        <h3>Información general del distribuidor</h3>
        <div className='distributor-grid distributor-general-grid'>
          {generalFields.map(([label, field]) => (
            <label className='distributor-field' key={field}>
              <span>{label}</span>
              <input className='form-control' value={generalFieldValue(field)} readOnly />
            </label>
          ))}
        </div>
      </section>

      <section className='distributor-section'>
        <h3>Documentación</h3>
        <div className='distributor-documents'>
          {documentFields
            .filter(([, field]) => distributor[field] || requiredDocumentFields.has(field))
            .map(([label, field]) => distributor[field] ? (
              <button type='button' className='distributor-document-button' key={field} onClick={() => window.open(distributor[field], '_blank', 'noopener,noreferrer')}>
                <i className='fas fa-file-download' aria-hidden='true'></i><span>{label}</span>
              </button>
            ) : (
              <div className='distributor-document-button distributor-document-unavailable' key={field} aria-label={`${label}: no disponible`}>
                <i className='fas fa-file' aria-hidden='true'></i><span>{label}<small>No disponible en este registro</small></span>
              </div>
            ))}
        </div>
      </section>

      <section className='distributor-section'>
        <h3>Detalles comerciales</h3>
        <div className='distributor-grid distributor-commercial-grid'>
          <label className='distributor-field'><span>Asignar vendedor</span><select className='form-select' value={form.idVendedor ?? ''} onChange={(e) => updateField('idVendedor', e.target.value)}><option value=''>Selecciona un vendedor</option>{Array.isArray(vendedores) && vendedores.map((item) => <option key={item.id} value={item.id}>{item.nombre} {item.apellido}</option>)}</select></label>
          <label className='distributor-field'><span>Lista de precios</span><select className='form-select' value={form.idListaPrecios ?? ''} onChange={(e) => updateField('idListaPrecios', e.target.value)}><option value=''>Selecciona una lista</option>{listasPrecios.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}</select></label>
          <label className='distributor-field'><span>Días de crédito</span><input className='form-control' type='number' min='0' step='1' value={form.diasCredito ?? ''} onChange={(e) => updateField('diasCredito', e.target.value)} /></label>
          <label className='distributor-field'><span>Cascada de descuentos</span><input className='form-control' type='text' maxLength='255' value={form.cascadaDescuentos ?? ''} onChange={(e) => updateField('cascadaDescuentos', e.target.value)} /></label>
          <label className='distributor-field'><span>Descuento total (%)</span><input className='form-control' type='number' min='0' step='0.01' value={form.descuentoTotal ?? ''} onChange={(e) => updateField('descuentoTotal', e.target.value)} /></label>
          <label className='distributor-field'><span>Condicionado 1 (%)</span><input className='form-control' type='number' min='0' step='0.01' value={form.condicionado1 ?? ''} onChange={(e) => updateField('condicionado1', e.target.value)} /></label>
          <label className='distributor-field'><span>Condición 1</span><input className='form-control' maxLength='255' value={form.condicion1 ?? ''} onChange={(e) => updateField('condicion1', e.target.value)} /></label>
          <label className='distributor-field'><span>Condicionado 2 (%)</span><input className='form-control' type='number' min='0' step='0.01' value={form.condicionado2 ?? ''} onChange={(e) => updateField('condicionado2', e.target.value)} /></label>
          <label className='distributor-field'><span>Condición 2</span><input className='form-control' maxLength='255' value={form.condicion2 ?? ''} onChange={(e) => updateField('condicion2', e.target.value)} /></label>
          <label className='distributor-field distributor-switch-field'><span>Mostrar descuento total</span><span className='form-check form-switch'><input className='form-check-input' type='checkbox' checked={Boolean(form.mostrarDescuentoTotal)} onChange={(e) => updateField('mostrarDescuentoTotal', e.target.checked)} /></span></label>
        </div>
      </section>

      <section className='distributor-section distributor-account-section'>
        <div>
          <h3>{distributor.estatus === 'Por Activar' ? 'Activación inicial' : 'Estado de la cuenta'}</h3>
          <p>{activationMessage}</p>
          <label className='distributor-field distributor-status'>
            <span>Estatus</span>
            <select
              className='form-select'
              value={form.estatus ?? ''}
              disabled={distributor.estatus === 'Por Activar'}
              onChange={(e) => updateField('estatus', e.target.value)}
            >
              {distributor.estatus === 'Por Activar'
                ? <option value='Por Activar'>Por activar</option>
                : <><option value='Activo'>Activo</option><option value='Inactivo'>Inactivo</option></>}
            </select>
          </label>
        </div>
        {distributor.estatus === 'Por Activar' && <div className='d-flex gap-2'>
          <button type='button' className='btn btn-outline-primary' disabled={activating || saving} onClick={activateAccount}>{activating ? 'Procesando...' : 'Enviar activación'}</button>
          <button type='button' className='btn btn-primary' disabled={activating || saving} onClick={() => {setManualActivation(true); setMessage(null)}}>Activar manualmente</button>
        </div>}

      </section>

      <section className='distributor-section'>
        <h3>{manualActivation && distributor.estatus === 'Por Activar' ? 'Activar manualmente' : 'Contraseña y seguridad'}</h3>
        {distributor.estatus === 'Por Activar' ? (
          manualActivation ? <div className='password-security-fields'>
            <PasswordSecurityFields newPassword={passwords.password} confirmPassword={passwords.confirmPassword}
              confirmPasswordLabel='Confirmar contraseña'
              onNewPasswordChange={(password) => setPasswords({...passwords, password})}
              onConfirmPasswordChange={(confirmPassword) => setPasswords({...passwords, confirmPassword})} />
            <div className='d-flex gap-2 mt-4'>
              <button type='button' className='btn btn-primary' disabled={activating || saving} onClick={activateManually}>{activating ? 'Activando...' : 'Confirmar activación'}</button>
              <button type='button' className='btn btn-secondary' disabled={activating || saving} onClick={() => {setManualActivation(false); setPasswords({password: '', confirmPassword: ''}); setMessage(null)}}>Cancelar activación</button>
            </div>
          </div> : <p className='mb-0'>El restablecimiento administrativo estará disponible cuando el distribuidor complete la activación inicial y cree su contraseña.</p>
        ) : (
          <div className='password-security-fields'>
            <PasswordSecurityFields required={false} newPassword={passwords.password} confirmPassword={passwords.confirmPassword}
              onNewPasswordChange={(password) => {setPasswords({...passwords, password}); setMessage(null)}}
              onConfirmPasswordChange={(confirmPassword) => {setPasswords({...passwords, confirmPassword}); setMessage(null)}} />
          </div>
        )}
      </section>

      {message && <div className={`alert alert-${message.type} distributor-message`} role='alert'>{message.text}</div>}
      <div className='distributor-actions'><button type='button' className='btn btn-primary' disabled={saving || activating || manualActivation} onClick={saveCommercialDetails}>{saving ? 'Guardando...' : 'Guardar cambios'}</button><button type='button' className='btn btn-secondary' disabled={saving || activating} onClick={onClose}>Cancelar</button></div>
    </div>
  )
}

export {ModalDistribuidor}
